import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useNavigate } from "react-router-dom";
import { Transition } from "../../Calendars/Calendars";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import {
  MappedSite,
  RemoteLinkSite,
  useSitesPageStore,
} from "./sitesPageStore/sitesPageStore";
import { UnlinkRemoteLinkSiteDialog } from "./UnlinkRemoteLinkSiteDialog";
import {
  filterRemoteLinkOptions,
  isSingleSiteOption,
  remoteLinkOptionLabel,
  RemoteLinkSiteLink,
} from "./remoteLinkSiteOptions";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { Autocomplete, TableBody, TextField } from "@mui/material";
import { ActuateButton } from "../../../common/ActuateButton";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

export const ImmixRemotelinkDialog = () => {
  const {
    immixRemotelinkDialogOpen,
    setImmixRemotelinkDialogOpen,
    remoteLinkSites,
    fetchingImmixRemoteLinkData,
    getImmixRemoteLinkData,
    mappedSites,
    unmappedSites,
    updateImmixRemoteLinkData,
    unlinkImmixRemoteLinkData,
    resetImmixRemoteLinkData,
    updatingImmixRemoteLinkData,
  } = useSitesPageStore();
  const navigate = useNavigate();

  const [localMappedSites, setLocalMappedSites] = React.useState<any>({});
  // Mapped-site row whose "Unlink" button was clicked; drives the confirm
  // dialog. null = closed.
  const [siteToUnlink, setSiteToUnlink] = React.useState<MappedSite | null>(
    null
  );

  React.useEffect(() => {
    resetImmixRemoteLinkData();
    getImmixRemoteLinkData(navigate);
  }, []);

  // Close the confirm as soon as the site has left Mapped Sites. The store
  // moves it there on success, including when the shared error handler's
  // retry (after a token refresh) is what succeeded, whose result the
  // onConfirm await never sees.
  React.useEffect(() => {
    if (siteToUnlink && !mappedSites.some((s) => s.id === siteToUnlink.id)) {
      setSiteToUnlink(null);
    }
  }, [mappedSites, siteToUnlink]);

  const siteOptions = React.useMemo(
    () => remoteLinkSites.filter(isSingleSiteOption),
    [remoteLinkSites]
  );

  return (
    <div className="absolute">
      <Dialog
        open={immixRemotelinkDialogOpen}
        TransitionComponent={Transition}
        fullScreen
      >
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
          open={updatingImmixRemoteLinkData}
        >
          <CircularProgress sx={{ color: "#FF8400" }} />
        </Backdrop>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar sx={{ backgroundColor: "#001943" }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setImmixRemotelinkDialogOpen(false);
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Immix Remotelink
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          {fetchingImmixRemoteLinkData ? null : (
            <>
              <p className="font-mulish text-[20px] font-bold text-actuate-blue">
                Mapped Sites
              </p>
              {mappedSites.length > 0 && (
                <TableContainer sx={{ width: 1000 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontFamily: "Mulish",
                            fontWeight: "bold",
                            fontSize: 18,
                            color: "#FF8400",
                            textAlign: "center",
                          }}
                        >
                          Immix Site
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Mulish",
                            fontWeight: "bold",
                            fontSize: 18,
                            color: "#FF8400",
                            textAlign: "center",
                          }}
                        >
                          Actuate Site
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Mulish",
                            fontWeight: "bold",
                            fontSize: 18,
                            color: "#FF8400",
                            textAlign: "center",
                            width: 140,
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mappedSites.map((site, index) => (
                        <TableRow
                          key={site.id}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#f5f5f5" : "transparent",
                          }}
                        >
                          <TableCell
                            sx={{
                              textAlign: "center",
                              borderLeft: "1px solid #e0e0e0",
                              borderRight: "1px solid #e0e0e0",
                              borderBottom: "1px solid #e0e0e0",
                              fontFamily: "Mulish",
                            }}
                          >
                            {site.immix_group_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              borderRight: "1px solid #e0e0e0",
                              borderBottom: "1px solid #e0e0e0",
                              fontFamily: "Mulish",
                            }}
                          >
                            {site.admin_group_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              borderRight: "1px solid #e0e0e0",
                              borderBottom: "1px solid #e0e0e0",
                              fontFamily: "Mulish",
                            }}
                          >
                            <button
                              type="button"
                              aria-label={`Unlink ${site.immix_group_name}`}
                              disabled={updatingImmixRemoteLinkData}
                              onClick={() => setSiteToUnlink(site)}
                              className="font-mulish text-base text-actuate-blue underline hover:text-[#E67300] disabled:text-[#A9A9A9] disabled:no-underline"
                            >
                              Unlink
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {mappedSites.length === 0 && (
                <p className="font-mulish text-[16px] text-actuate-blue mt-[20px]">
                  No mapped Immix sites found.
                </p>
              )}
              <p className="font-mulish text-[20px] font-bold text-actuate-blue mt-[35px]">
                Unmapped Sites
              </p>
              {unmappedSites.length > 0 && (
                <div>
                  <p className="font-mulish text-[16px] text-actuate-blue">
                    Please map the following Immix sites to Actuate sites to
                    ensure signals are being received properly.
                  </p>
                  <TableContainer sx={{ width: 1000 }}>
                    <Table sx={{ tableLayout: "fixed" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontFamily: "Mulish",
                              fontWeight: "bold",
                              fontSize: 18,
                              color: "#FF8400",
                              textAlign: "center",
                              width: "50%",
                            }}
                          >
                            Immix Site
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "Mulish",
                              fontWeight: "bold",
                              fontSize: 18,
                              color: "#FF8400",
                              textAlign: "center",
                              width: "50%",
                            }}
                          >
                            Actuate Site
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {unmappedSites.map((site, index) => (
                          <TableRow
                            key={site.id}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#f5f5f5" : "transparent",
                            }}
                          >
                            <TableCell
                              sx={{
                                textAlign: "center",
                                borderLeft: "1px solid #e0e0e0",
                                borderRight: "1px solid #e0e0e0",
                                borderBottom: "1px solid #e0e0e0",
                                fontFamily: "Mulish",
                                width: "50%",
                              }}
                            >
                              {site.immix_group_name}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "center",
                                borderRight: "1px solid #e0e0e0",
                                borderBottom: "1px solid #e0e0e0",
                                fontFamily: "Mulish",
                                width: "50%",
                              }}
                            >
                              <div className="flex">
                                <Autocomplete
                                  disablePortal
                                  options={siteOptions}
                                  getOptionLabel={remoteLinkOptionLabel}
                                  filterOptions={filterRemoteLinkOptions}
                                  isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                  }
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                      <div className="flex w-full items-center gap-[10px]">
                                        <span className="grow text-left">
                                          {option.name}
                                        </span>
                                        {option.site_id != null && (
                                          <span className="shrink-0 text-[12px] text-[#6b7280]">
                                            Site #{option.site_id}
                                          </span>
                                        )}
                                      </div>
                                    </li>
                                  )}
                                  onChange={(
                                    event: any,
                                    newInputValue: RemoteLinkSite | null
                                  ) => {
                                    if (newInputValue) {
                                      setLocalMappedSites({
                                        ...localMappedSites,
                                        [site.id]: {
                                          id: site.id,
                                          admin_group_id: newInputValue.id,
                                          site_id:
                                            newInputValue.site_id ?? null,
                                        },
                                      });
                                    } else {
                                      const next = { ...localMappedSites };
                                      delete next[site.id];
                                      setLocalMappedSites(next);
                                    }
                                  }}
                                  sx={{ width: "450px" }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      autoComplete="off"
                                      placeholder="Search by site name or number"
                                    />
                                  )}
                                />
                                <div className="ml-[15px]"></div>
                                {localMappedSites[site.id]?.site_id != null && (
                                  <div className="mr-[15px] self-center whitespace-nowrap">
                                    <RemoteLinkSiteLink
                                      siteId={localMappedSites[site.id].site_id}
                                      label={`View site #${
                                        localMappedSites[site.id].site_id
                                      } ↗`}
                                    />
                                  </div>
                                )}
                                <ActuateButton
                                  disabled={
                                    !localMappedSites[site.id] ||
                                    !localMappedSites[site.id].admin_group_id
                                  }
                                  text="Save"
                                  onClick={() => {
                                    // console.log(localMappedSites[site.immix_group_id]);
                                    updateImmixRemoteLinkData(
                                      localMappedSites[site.id],
                                      navigate
                                    );
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
              {unmappedSites.length === 0 && (
                <p className="font-mulish text-[16px] text-actuate-blue mt-[20px]">
                  No unmapped Immix sites found.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <UnlinkRemoteLinkSiteDialog
        site={siteToUnlink}
        busy={updatingImmixRemoteLinkData}
        onCancel={() => setSiteToUnlink(null)}
        onConfirm={async () => {
          if (!siteToUnlink) return;
          const ok = await unlinkImmixRemoteLinkData(siteToUnlink, navigate);
          if (ok) setSiteToUnlink(null);
        }}
      />
    </div>
  );
};
